import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { UserService } from '../../../../services/user.service';
import { SectionsService } from '../../../../services/sections.service';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { DatePickerComponent } from '../../../../shared/components/date-picker/date-picker.component';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';
import { CommonModule, DatePipe } from '@angular/common';
import { AuthorizationService } from '../../../../services/authorization.service';
import { performValidation } from '../../../../shared/utils/validation.util';

@Component({
  selector: 'ppl-auth-allocation',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzGridModule,
    ButtonComponent,
    DatePickerComponent,
    NzInputModule,
    NzToolTipModule,
    DropdownSelectComponent,
    CommonModule,
  ],
  templateUrl: './auth-allocation.component.html',
  styleUrl: './auth-allocation.component.css',
})
export class AuthAllocationComponent extends BaseComponent implements OnInit {
  authAllocationForm!: FormGroup;
  programName: any;
  serviceCodeList: any;
  providerList: any;
  mcoEntries: any;
  minStartDate?: Date;
  minDate?: Date;
  maxEndDate?: Date;
  participantId: any;
  userDetails: any;
  minSteps = [
    { value: '00', label: '00' },
    { value: '15', label: '15' },
    { value: '30', label: '30' },
    { value: '45', label: '45' },
  ];
  @Output() cancel = new EventEmitter<any>();
  @Output() savedWeeklyLimit = new EventEmitter<any>();
  @Input() mode: any;
  @Input() authData: any;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private sectionService: SectionsService,
    private authorizationService: AuthorizationService,
    private datePipe: DatePipe
  ) {
    super();
  }

  ngOnInit(): void {
    this.authAllocationForm = this.fb.group({
      servicecodeDetailId: [''],
      providerId: [''],
      startDate: [''],
      endDate: [''],
      weeklyLimitHrs: [''],
      weeklyLimitMins: [null],
      comments: [''],
      orgId: [''],
      units: [{ value: null, disabled: true }],
    });

    const program = this.userService.getSelectedProgram();
    this.programName = program?.program_code ?? '';
    this.secData = this.sectionService.getSectionData(
      program?.program_code,
      'authAllocation'
    );
    this.userDetails = this.userService.getUserDetails();
    const userOrgId = this.userDetails?.belongsto_org;
    const isMCOUser = this.userDetails?.usertype_lkp?.toLowerCase() === 'mco';
    const isLDSSUser = this.userDetails?.usertype_lkp?.toLowerCase() === 'ldss';

    if (isMCOUser) {
      this.authAllocationForm.get('orgId')?.setValue(userOrgId);
      this.authAllocationForm.get('orgId')?.disable();
    } else if (isLDSSUser) {
      this.authAllocationForm.get('orgId')?.setValue(143);
      this.authAllocationForm.get('orgId')?.disable();
    }

    if (this.mode === 'edit') {
      this.minDate = undefined; // allow past dates
    } else {
      this.minDate = new Date();
      this.minDate.setHours(0, 0, 0, 0);
      this.minDate.setDate(this.minDate.getDate() + 1); // tomorrow
    }
    this.participantId = this.userService.getSelectedLead();
    this.getDropdownValues();
    if (this.mode === 'edit') {
      const data = { ...this.authData };

      if (data.hours && typeof data.hours === 'string') {
        const [hrs, mins] = data.hours.split(':');
        data.weeklyLimitHrs = hrs;
        data.weeklyLimitMins = mins;
      }
      this.authAllocationForm.patchValue(data);
      this.authAllocationForm.disable();
      if (data.isFuture) {
        this.authAllocationForm.get('endDate')?.enable();
        this.authAllocationForm.get('startDate')?.enable();
        this.authAllocationForm.get('weeklyLimitHrs')?.enable();
        this.authAllocationForm.get('weeklyLimitMins')?.enable();
      } else {
        this.authAllocationForm.get('endDate')?.enable();
      }
    }
    this.authAllocationForm.get('endDate')?.valueChanges.subscribe(() => {
      this.updateMinStartDate();
    });
    this.authAllocationForm.get('startDate')?.valueChanges.subscribe(() => {
      this.updateMaxEndDate();
    });
    this.authAllocationForm
      .get('weeklyLimitHrs')
      ?.valueChanges.subscribe((hours) => {
        this.updateUnits(
          hours,
          this.authAllocationForm.get('weeklyLimitMins')?.value
        );
      });

    this.authAllocationForm
      .get('weeklyLimitMins')
      ?.valueChanges.subscribe((mins) => {
        this.updateUnits(
          this.authAllocationForm.get('weeklyLimitHrs')?.value,
          mins
        );
      });
  }

  updateMinStartDate(): void {
    const endDate = this.authAllocationForm.get('endDate')?.value;
    if (endDate instanceof Date) {
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() - 1);
      this.minStartDate = nextDay;
    } else {
      this.minStartDate = undefined;
    }
  }

  updateMaxEndDate(): void {
    const startDate = this.authAllocationForm.get('startDate')?.value;
    if (startDate instanceof Date) {
      const nextDay = new Date(startDate);
      nextDay.setDate(nextDay.getDate() + 1);
      this.maxEndDate = nextDay;
    } else {
      this.maxEndDate = undefined;
    }
  }

  onUnitMinChange(event: any) {
    if (
      event &&
      (this.mode !== 'edit' || (this.mode === 'edit' && this.authData.isFuture))
    ) {
      let hours = this.authAllocationForm.get('weeklyLimitHrs')?.value;
      if (!hours && event) {
        hours = 0;
      }
      const totalAuthUnits =
        hours.toString().padStart(2, '0') + event?.toString().padStart(2, '0');
      this.authAllocationForm.get('units')?.setValue(totalAuthUnits);
      const totalUnits = this.timeToUnits(totalAuthUnits);
      this.authAllocationForm.get('units')?.setValue(totalUnits);
    }
  }

  onUnitHrChange(event: any) {
    if (
      this.mode !== 'edit' ||
      (this.mode === 'edit' && this.authData.isFuture)
    ) {
      let mins = this.authAllocationForm.get('weeklyLimitMins')?.value;
      if (!mins && event) {
        mins = 0;
      }
      const totalAuthUnits =
        event?.toString().padStart(2, '0') + mins?.toString().padStart(2, '0');
      this.authAllocationForm.get('units')?.setValue(totalAuthUnits);
      const totalUnits = this.timeToUnits(totalAuthUnits);
      this.authAllocationForm.get('units')?.setValue(totalUnits);
    }
  }

  updateUnits(hours: any, mins: any) {
    if (
      this.mode !== 'edit' ||
      (this.mode === 'edit' && this.authData.isFuture)
    ) {
      if (!hours) hours = 0;
      if (!mins) mins = 0;
      const totalAuthUnits =
        hours.toString().padStart(2, '0') + mins.toString().padStart(2, '0');
      const totalUnits = this.timeToUnits(totalAuthUnits);
      this.authAllocationForm.get('units')?.setValue(totalUnits);
    }
  }

  timeToUnits(time: string): number {
    if (time) {
      const minutes = parseInt(time.slice(-2), 10);
      const hours = parseInt(time.slice(0, -2), 10);
      const totalMinutes = hours * 60 + minutes;
      const units = Math.floor(totalMinutes / 15);
      return units;
    } else {
      return 0;
    }
  }

  limitDigits(event: any): void {
    const value = event.target.value.replace(/[^0-9]/g, '');
    event.target.value = value;
    this.authAllocationForm.get('weeklyLimitHrs')?.setValue(value);
  }

  getDropdownValues() {
    this.authorizationService
      .getDropdownDetails(this.programName, this.participantId)
      .subscribe((data: any) => {
        this.serviceCodeList = data.responsedata.svcCodes;
        this.providerList = data.responsedata.providers;
        this.mcoEntries = Array.from(
          new Map(
            this.serviceCodeList.map((item: any) => [item.orgId, { orgId: item.orgId, orgName: item.orgName }])
          ).values()
        );
    });
  }

  cancelEdit() {
    this.cancel.emit(true);
  }

  save() {
    const formData = this.authAllocationForm.getRawValue();
    let noUnits = false;
    noUnits =
      !formData.units ||
      formData.units === '0000' ||
      formData.units === '0' ||
      formData.units === 0 ||
      isNaN(Number(formData.units));
    this.validationErrors = performValidation(
      formData,
      this.secData,
      'Next',
      undefined,
      undefined,
      undefined,
      undefined
    );
    let newError: any = {};
    if (noUnits) {
      newError = {
        weeklyLimitHrs: 'Please enter Weekly Limit.',
      };
    }

    this.validationErrors = {
      ...this.validationErrors,
      ...newError,
    };
    if (Object.keys(this.validationErrors).length === 0) {
      if (this.mode === 'edit') {
        this.update();
      } else {
        const data = this.authAllocationForm.getRawValue();
        const payload = {
          ...data,
          startDate: this.datePipe.transform(data.startDate, 'MM/dd/yyyy'),
          endDate: this.datePipe.transform(data.endDate, 'MM/dd/yyyy'),
          units: data.units,
          participantId: this.participantId,
          unitType: null,
          sourceOrgId: data.orgId,
        };

        this.authorizationService
          .saveWeeklyLimit(payload, this.programName)
          .subscribe((data: any) => {
            this.savedWeeklyLimit.emit(true);
            this.cancel.emit(true);
          });
      }
    } else {
      return;
    }
  }

  update() {
    const data = this.authAllocationForm.getRawValue();
    const payload = {
      tasAuthorizationXProviderId: this.authData.tasAuthorizationXProviderId,
      units: data.units,
      endDate: this.datePipe.transform(data.endDate, 'MM/dd/yyyy'),
      startDate: this.datePipe.transform(data.startDate, 'MM/dd/yyyy'),
      comments: data.comments,
    };
    this.authorizationService
      .updateWeeklyLimit(payload, this.programName)
      .subscribe((data: any) => {
        this.savedWeeklyLimit.emit(true);
        this.cancel.emit(true);
      });
  }
}
